/**
 * Billing Module - BillingHome Page
 * 
 * Main billing page showing all transactions for the tenant
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@trafiflow/shared-ui'
import { Card } from '@trafiflow/shared-ui'
import apiClient from '../../../shared/lib/api'
import type { BillingTransaction, Contract } from '../types'
import CancelContractModal from '../components/CancelContractModal'

export default function BillingHome() {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState<BillingTransaction[]>([])
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loading, setLoading] = useState(true)
  const [contractsLoading, setContractsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [contractsError, setContractsError] = useState<string | null>(null)
  const [balance, setBalance] = useState<number>(0)
  const [cancellingContractId, setCancellingContractId] = useState<string | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [contractToCancel, setContractToCancel] = useState<Contract | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user?.tenantId) {
        setError('Tenant ID not found')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        console.log('[BillingHome] Fetching transactions for tenant:', user?.tenantId)
        const response = await apiClient.get('/billing/transactions')
        console.log('[BillingHome] Response received:', response.status, response.data)
        
        const data = response.data

        if (data.transactions) {
          setTransactions(data.transactions)
          // Calculate balance from transactions
          const calculatedBalance = data.transactions.reduce((acc: number, tx: BillingTransaction) => {
            if (tx.type === 'credit') {
              return acc + tx.amount
            } else {
              return acc - tx.amount
            }
          }, 0)
          setBalance(calculatedBalance)
        } else {
          // If no transactions field, initialize empty
          setTransactions([])
          setBalance(0)
        }
      } catch (error: unknown) {
      const err = error as { response?: { status?: number; statusText?: string; data?: { message?: string; error?: string } | string }; message?: string; config?: { url?: string; method?: string; baseURL?: string } }
        console.error('[BillingHome] Error fetching transactions:', err)
        console.error('[BillingHome] Error details:', {
          message: err.message,
          response: err.response,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          config: {
            url: err.config?.url,
            method: err.config?.method,
            baseURL: err.config?.baseURL,
          },
        })
        
        let errorMessage = 'Failed to load transactions'
        if (err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message
          }
        } else if (err.message) {
          errorMessage = err.message
        }
        
        // If 404, the endpoint might not exist yet
        if (err.response?.status === 404) {
          errorMessage = 'Billing endpoint not found. Please ensure the API is deployed.'
        }
        
        setError(errorMessage)
        // Initialize empty state on error
        setTransactions([])
        setBalance(0)
      } finally {
        setLoading(false)
      }
    }

    fetchTransactions()
  }, [user?.tenantId])

  useEffect(() => {
    const fetchContracts = async () => {
      if (!user?.tenantId) {
        setContractsError('Tenant ID not found')
        setContractsLoading(false)
        return
      }

      try {
        setContractsLoading(true)
        setContractsError(null)
        
        console.log('[BillingHome] Fetching contracts for tenant:', user?.tenantId)
        const response = await apiClient.get('/billing/contracts')
        console.log('[BillingHome] Contracts response received:', response.status, response.data)
        
        const data = response.data

        if (data.contracts) {
          setContracts(data.contracts)
        } else {
          setContracts([])
        }
      } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string; error?: string } | string }; message?: string }
        console.error('[BillingHome] Error fetching contracts:', err)
        
        let errorMessage = 'Failed to load contracts'
        if (err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message
          }
        } else if (err.message) {
          errorMessage = err.message
        }
        
        // If 404, the endpoint might not exist yet - don't show error, just empty state
        if (err.response?.status === 404) {
          errorMessage = ''
          setContracts([])
        } else {
          setContractsError(errorMessage)
        }
      } finally {
        setContractsLoading(false)
      }
    }

    fetchContracts()
  }, [user?.tenantId])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleCancelClick = (contract: Contract) => {
    setContractToCancel(contract)
    setCancelError(null)
    setShowCancelModal(true)
  }

  const handleCancelConfirm = async () => {
    if (!contractToCancel) return

    try {
      setCancellingContractId(contractToCancel.id)
      const response = await apiClient.put(`/billing/contracts/${contractToCancel.id}/cancel`)
      console.log('[BillingHome] Contract cancelled successfully:', response.data)
      
      // Update the contract in the list
      setContracts(contracts.map(contract => 
        contract.id === contractToCancel.id 
          ? { ...contract, status: 'cancelled' as const, updated_at: response.data.contract.updated_at }
          : contract
      ))
      
      // Close modal and reset state
      setShowCancelModal(false)
      setContractToCancel(null)
      setCancelError(null)
    } catch (err: unknown) {
      console.error('[BillingHome] Error cancelling contract:', err)
      const error = err as { response?: { data?: { error?: string } }; message?: string }
      const errorMessage = error.response?.data?.error || error.message || 'Failed to cancel contract'
      setCancelError(errorMessage)
    } finally {
      setCancellingContractId(null)
    }
  }

  const handleCancelClose = () => {
    if (!cancellingContractId) {
      setShowCancelModal(false)
      setContractToCancel(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-tf-text-muted">Loading transactions...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="p-6">
          <div className="text-red-600">Error: {error}</div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-tf-text mb-2">Billing</h1>
        <p className="text-tf-text-muted">View your account transactions and balance</p>
      </div>

      {/* Balance Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-medium text-tf-text-muted mb-1">Current Balance</h2>
            <p className="text-3xl font-bold text-tf-text">{formatCurrency(balance)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-tf-text-muted">Total Transactions</p>
            <p className="text-2xl font-semibold text-tf-text-sub">{transactions.length}</p>
          </div>
        </div>
      </Card>

      {/* Contracts Section */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-tf-text mb-4">Contracts</h2>
        
        {contractsLoading ? (
          <div className="text-center py-8 text-tf-text-muted">
            <p>Loading contracts...</p>
          </div>
        ) : contractsError ? (
          <div className="text-center py-8 text-red-600">
            <p>Error: {contractsError}</p>
          </div>
        ) : contracts.length === 0 ? (
          <div className="text-center py-12 text-tf-text-muted">
            <p>No contracts found</p>
            <p className="text-sm mt-2">Your agent contracts will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-tf-border">
              <thead className="bg-tf-bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Next Billing Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-tf-bg-card divide-y divide-tf-border">
                {contracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-tf-bg-muted">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-tf-text font-medium">
                      {contract.agent_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-tf-text">
                      {formatDateShort(contract.start_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          contract.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : contract.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-tf-bg-hover text-tf-text'
                        }`}
                      >
                        {contract.status === 'active' ? 'Active' : contract.status === 'cancelled' ? 'Cancelled' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-tf-text font-medium">
                      {formatCurrency(contract.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-tf-text">
                      {formatDateShort(contract.next_billing_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {contract.status === 'active' && (
                        <button
                          onClick={() => handleCancelClick(contract)}
                          disabled={cancellingContractId === contract.id}
                          className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-tf-feedback-error-bg rounded-md border border-red-300 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Transactions Table */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-tf-text mb-4">Transaction History</h2>
        
        {transactions.length === 0 ? (
          <div className="text-center py-12 text-tf-text-muted">
            <p>No transactions found</p>
            <p className="text-sm mt-2">Your transaction history will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-tf-border">
              <thead className="bg-tf-bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-tf-bg-card divide-y divide-tf-border">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-tf-bg-muted">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-tf-text">
                      {formatDate(transaction.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.type === 'credit'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {transaction.type === 'credit' ? 'Credit' : 'Debit'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-tf-text">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-tf-text-muted">
                      {transaction.reference || '-'}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                        transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {transaction.type === 'credit' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Cancel Contract Modal */}
      {contractToCancel && (
        <CancelContractModal
          isOpen={showCancelModal}
          contractName={contractToCancel.agent_name}
          onClose={handleCancelClose}
          onConfirm={handleCancelConfirm}
          isLoading={cancellingContractId === contractToCancel.id}
          error={cancelError}
        />
      )}
    </div>
  )
}
